import { useEffect, useMemo, useState } from 'react';
import { listPackages, getPackage, updatePackageItinerary } from '../../api/adminPackages';
import Button from '../../components/shared/ui/Button';

const AdminPackageItineraries = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listPackages();
      setItems(res?.data || res || []);
    } catch (err) {
      setError(err.message || 'Failed to load itineraries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => item.title?.toLowerCase().includes(q));
  }, [items, search]);

  const selectPackage = async (pkg) => {
    if (!pkg?.id) return;
    setError('');
    try {
      const res = await getPackage(pkg.id);
      const data = res?.data || res;
      const rows = (data.itinerary || []).map((row) => ({
        dayNumber: row.day_number,
        dayTitle: row.day_title,
        description: row.description || '',
        imageUrl: row.image_url || '',
      }));
      setSelected({ id: data.id, title: data.title, destination: data.destination_name || data.destination_city });
      setItinerary(rows.length ? rows : [{ dayNumber: 1, dayTitle: 'Day 1', description: '', imageUrl: '' }]);
    } catch (err) {
      setError(err.message || 'Unable to load package itinerary');
    }
  };

  const addDay = () => {
    setItinerary((prev) => [
      ...prev,
      { dayNumber: prev.length + 1, dayTitle: `Day ${prev.length + 1}`, description: '', imageUrl: '' },
    ]);
  };

  const updateDay = (index, key, value) => {
    setItinerary((prev) => prev.map((row, idx) => (idx === index ? { ...row, [key]: value } : row)));
  };

  const removeDay = (index) => {
    setItinerary((prev) => prev.filter((_, idx) => idx !== index));
  };

  const saveItinerary = async () => {
    if (!selected?.id) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        itinerary: itinerary.map((row, idx) => ({
          day_number: row.dayNumber || idx + 1,
          day_title: row.dayTitle || `Day ${idx + 1}`,
          description: row.description || '',
          image_url: row.imageUrl || '',
        })),
      };
      await updatePackageItinerary(selected.id, payload);
    } catch (err) {
      setError(err.message || 'Failed to save itinerary');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Tours & Packages</p>
        <h1 className="text-xl font-semibold text-slate-900">Itineraries & Activities</h1>
        <p className="mt-2 text-sm text-slate-600">Manage day-wise itinerary inside each package.</p>
      </div>
      {error && <div className="rounded-2xl border border-rose-100 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        <div className="space-y-3">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search packages"
            className="w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
          />
          <div className="rounded-2xl border border-slate-200 bg-white">
            <div className="max-h-[520px] overflow-auto p-2">
              {loading ? (
                <div className="p-4 text-sm text-slate-500">Loading packages...</div>
              ) : filteredItems.length ? (
                filteredItems.map((pkg) => (
                  <button
                    key={pkg.id}
                    type="button"
                    onClick={() => selectPackage(pkg)}
                    className={`flex w-full flex-col gap-1 rounded-xl px-3 py-2 text-left text-sm transition ${
                      selected?.id === pkg.id ? 'bg-slate-100 text-slate-900' : 'hover:bg-slate-50'
                    }`}
                  >
                    <span className="font-semibold text-slate-900">{pkg.title}</span>
                    <span className="text-xs text-slate-500">{pkg.destination_name || pkg.destination_city}</span>
                  </button>
                ))
              ) : (
                <div className="p-4 text-sm text-slate-500">No packages yet.</div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5">
          {!selected ? (
            <div className="text-sm text-slate-500">Select a package to edit its itinerary.</div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Package</p>
                  <h2 className="text-lg font-semibold text-slate-900">{selected.title}</h2>
                  <p className="text-xs text-slate-500">{selected.destination || '-'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={addDay}>
                    + Add day
                  </Button>
                  <Button size="sm" onClick={saveItinerary} disabled={saving}>
                    {saving ? 'Saving...' : 'Save itinerary'}
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {itinerary.map((day, idx) => (
                  <div key={`day-${idx}`} className="rounded-2xl border border-slate-200 p-4">
                    <div className="grid gap-2 md:grid-cols-[120px_1fr]">
                      <input
                        className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                        placeholder="Day #"
                        value={day.dayNumber}
                        onChange={(e) => updateDay(idx, 'dayNumber', e.target.value)}
                      />
                      <input
                        className="rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                        placeholder="Day title"
                        value={day.dayTitle}
                        onChange={(e) => updateDay(idx, 'dayTitle', e.target.value)}
                      />
                    </div>
                    <textarea
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                      rows={3}
                      placeholder="Description"
                      value={day.description}
                      onChange={(e) => updateDay(idx, 'description', e.target.value)}
                    />
                    <input
                      className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-2 text-sm"
                      placeholder="Image URL (optional)"
                      value={day.imageUrl}
                      onChange={(e) => updateDay(idx, 'imageUrl', e.target.value)}
                    />
                    <div className="mt-2 text-right">
                      <button type="button" onClick={() => removeDay(idx)} className="text-xs text-rose-500">
                        Remove day
                      </button>
                    </div>
                  </div>
                ))}
                {!itinerary.length && (
                  <div className="text-xs text-slate-500">No itinerary yet. Add your first day.</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPackageItineraries;
